import type { Priority, ParsedTaskInput } from '../components/todo/todoTypes';

/**
 * NLP Date Parser - regex heuristics for common date patterns.
 * NOT real NLP. Just fast pattern matching.
 */

// parseDate

export function parseDate(input: string): Date | null {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const lower = input.toLowerCase().trim();

  // "due:2026-03-15" or "due:tomorrow" - strip "due:" prefix and recurse
  const dueMatch = lower.match(/^due:(\S+)/);
  if (dueMatch) {
    return parseDate(dueMatch[1]!);
  }

  // ISO date format: YYYY-MM-DD (e.g. "2026-04-15")
  const isoMatch = lower.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (isoMatch) {
    const d = new Date(isoMatch[1]! + 'T00:00:00Z');
    if (!isNaN(d.getTime())) return d;
  }

  // "today"
  if (/\btoday\b/.test(lower)) {
    return new Date(today);
  }

  // "tomorrow"
  if (/\btomorrow\b/.test(lower)) {
    const r = new Date(today);
    r.setDate(r.getDate() + 1);
    return r;
  }

  // "next week"
  if (/\bnext week\b/.test(lower)) {
    const r = new Date(today);
    r.setDate(r.getDate() + 7);
    return r;
  }

  // "in X days"
  const inDaysMatch = lower.match(/\bin (\d+) days?\b/);
  if (inDaysMatch) {
    const r = new Date(today);
    r.setDate(r.getDate() + parseInt(inDaysMatch[1]!, 10));
    return r;
  }

  // Day-of-week patterns: "monday", "fri", "next friday", "next monday"
  const dayNames: [string[], number][] = [
    [['sunday', 'sun'], 0],
    [['monday', 'mon'], 1],
    [['tuesday', 'tue'], 2],
    [['wednesday', 'wed'], 3],
    [['thursday', 'thu'], 4],
    [['friday', 'fri'], 5],
    [['saturday', 'sat'], 6],
  ];

  // "next [day]"
  for (const [names, dayIndex] of dayNames) {
    const nextPattern = names.map((n) => `next ${n}`).join('|');
    const regex = new RegExp(`\\b(${nextPattern})\\b`, 'i');
    if (regex.test(lower)) {
      return nextDayOfWeek(dayIndex, today, 1); // next occurrence (minimum 7 days out)
    }
  }

  // bare "monday", "fri", etc. - upcoming occurrence
  for (const [names, dayIndex] of dayNames) {
    const regex = new RegExp(`\\b(${names.join('|')})\\b`, 'i');
    if (regex.test(lower)) {
      return nextDayOfWeek(dayIndex, today, 0); // next occurrence >= today
    }
  }

  // "fri 15" / "mar 15" / "15 march" / "15 march 2026"
  const monthNames = 'jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec';

  // "fri 15" or "mar 15"
  let match = lower.match(new RegExp(`\\b(\\w+) (\\d{1,2})\\b`));
  if (match) {
    const monthPart = match[1]!;
    const day = parseInt(match[2]!, 10);
    if (monthNames.includes(monthPart)) {
      return makeDate(monthPart, day, today.getFullYear());
    }
  }

  // "15 march" or "15 march 2026"
  match = lower.match(new RegExp(`(\\d{1,2}) (${monthNames})(?: (\\d{4}))?`));
  if (match) {
    const day = parseInt(match[1]!, 10);
    const monthPart = match[2]!;
    const year = match[3] ? parseInt(match[3]!, 10) : today.getFullYear();
    return makeDate(monthPart, day, year);
  }

  // "15/03" or "15/03/2026" (DD/MM or DD/MM/YY)
  match = lower.match(/(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/);
  if (match) {
    const day = parseInt(match[1]!, 10);
    const month = parseInt(match[2]!, 10);
    let year = match[3] ? parseInt(match[3]!, 10) : today.getFullYear();
    if (match[3] && match[3]!.length === 2) year = 2000 + year;
    return makeDateFromMonthDay(month - 1, day, year); // month is 0-indexed
  }

  return null;
}

function nextDayOfWeek(targetDay: number, from: Date, minDaysAhead: number): Date {
  const r = new Date(from);
  r.setDate(r.getDate() + minDaysAhead);
  const current = r.getDay();
  let diff = targetDay - current;
  if (diff <= 0) diff += 7;
  r.setDate(r.getDate() + diff);
  return r;
}

const MONTH_MAP: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

function makeDate(monthName: string, day: number, year: number): Date | null {
  const monthIndex = MONTH_MAP[monthName.toLowerCase()];
  if (monthIndex === undefined) return null;
  return makeDateFromMonthDay(monthIndex, day, year);
}

function makeDateFromMonthDay(monthIndex: number, day: number, year: number): Date | null {
  // Validate
  if (monthIndex < 0 || monthIndex > 11) return null;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;
  return new Date(year, monthIndex, day);
}

// parseTaskInput

/**
 * Parse a quick-add input string into structured fields.
 * Examples:
 *   "Call John tomorrow p1 #Work"  -> { title: "Call John", date: tomorrow, priority: 1, project: "work" }
 *   "Buy groceries @Sarah"         -> { title: "Buy groceries", assignee: "Sarah" }
 */
export function parseTaskInput(input: string): ParsedTaskInput {
  const lower = input.toLowerCase();
  let text = input;

  // Priority: p1, p2, p3, p4 or p:1, p:2, p:3, p:4 (default p3 if not specified)
  let priority: Priority = 3;
  const priorityMatch = lower.match(/\bp:?([1-4])\b/);
  if (priorityMatch) {
    priority = parseInt(priorityMatch[1]!, 10) as Priority;
    text = text.replace(priorityMatch[0], '').trim();
  }

  // Date
  const date = parseDate(input);

  // Project: #projectname
  let project: string | null = null;
  const projectMatch = input.match(/#(\w+)/);
  if (projectMatch) {
    project = projectMatch[1]!.toLowerCase();
    text = text.replace(projectMatch[0], '').trim();
  }

  // Assignee: @name
  let assignee: string | null = null;
  const assigneeMatch = input.match(/@(\w+)/);
  if (assigneeMatch) {
    assignee = assigneeMatch[1]!;
    text = text.replace(assigneeMatch[0], '').trim();
  }

  // Clean up extra whitespace
  text = text.replace(/\s+/g, ' ').trim();

  return { title: text, date, priority, project, assignee };
}
